import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, CaretDown, CheckCircle, PaperPlaneRight, Quotes, Sparkle, Star, Users } from "@phosphor-icons/react";
import { trackConversion } from "../../lib/analytics";
import client from "../../lib/api";
import { FAQS } from "./data";

/* ─────────────────────  PRICING PREVIEW  ───────────────────── */

const PRICING_TIERS = [
  {
    name: "Solo",
    price: "$79",
    period: "/ month",
    tagline: "1 truck · owner-operator",
    features: [
      "AI dispatch inbox + broker emails",
      "Branded PDF invoicing",
      "Driver SMS updates",
      "Live load tracking",
    ],
    cta: "Start free trial",
    href: "/pricing?tier=solo",
    featured: false,
  },
  {
    name: "Small Fleet",
    price: "$299",
    period: "/ month",
    tagline: "2–10 trucks · most popular",
    features: [
      "Everything in Solo",
      "AI Rate Negotiator (~$120/load)",
      "Broker credit + FMCSA verification",
      "IFTA state-mileage reports",
      "Multi-user with roles",
    ],
    cta: "Start free trial",
    href: "/pricing?tier=small-fleet",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    tagline: "50+ trucks · white-glove",
    features: [
      "Everything in Small Fleet",
      "Custom AI training on your history",
      "QuickBooks + ELD deep integration",
      "SSO + audit logs + priority SLA",
      "Dedicated CX manager",
    ],
    cta: "Talk to sales",
    href: "/enterprise",
    featured: false,
  },
];

export function PricingPreview() {
  return (
    <section
      id="pricing-preview"
      data-testid="pricing-preview-section"
      className="border-b border-white/10 py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            One flat monthly fee.{" "}
            <span className="text-[#FF5A1F]">No per-load surcharges.</span>
          </h2>
          <p className="text-neutral-400 mt-4 text-sm sm:text-base leading-relaxed">
            Every plan starts with a 7-day free trial — card required, $0 today, cancel anytime before day 8.
            Cancel anytime, keep your data for 30 days after.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {PRICING_TIERS.map((tier, i) => (
            <div
              key={tier.name}
              data-testid={`pricing-tier-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-2xl border p-6 sm:p-7 flex flex-col ${
                tier.featured
                  ? "border-[#FF5A1F]/40 bg-gradient-to-b from-[#FF5A1F]/[0.06] to-transparent"
                  : "border-white/10 bg-white/[0.02]"
              }`}
            >
              {tier.featured && (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF5A1F] text-black text-[10px] uppercase tracking-widest font-bold">
                  <Sparkle size={10} weight="fill" /> Most picked
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                {tier.name}
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                  {tier.price}
                </span>
                {tier.period && (
                  <span className="text-sm text-neutral-500">{tier.period}</span>
                )}
              </div>
              <div className="text-xs text-neutral-500 mb-6">{tier.tagline}</div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-200">
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className={tier.featured ? "text-[#FF5A1F] mt-0.5 shrink-0" : "text-emerald-400 mt-0.5 shrink-0"}
                    />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={tier.href}
                onClick={() => trackConversion("pricing_preview_cta", { tier: tier.name })}
                data-testid={`pricing-tier-cta-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                  tier.featured
                    ? "bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
                    : "border border-white/15 text-white hover:border-white/40 hover:bg-white/5"
                }`}
              >
                {tier.cta} <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/pricing"
            data-testid="pricing-see-full-link"
            className="text-sm text-neutral-400 hover:text-[#FF9764] inline-flex items-center gap-1.5"
          >
            See full feature comparison & 50-truck+ plans
            <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  TESTIMONIALS  ───────────────────── */

/* Inline waitlist form — swapped in on the pilot-cohort tile when the
   cohort is full. Captures email + fleet size so cohort 2 has a ready
   queue. No auth, no toast library on landing — inline success state
   only so the form is self-contained. */
export function PilotWaitlistForm() {
  const [email, setEmail] = useState("");
  const [fleetSize, setFleetSize] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // null | 'joined' | 'updated' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      const r = await client.post("/founding-pilots/waitlist", {
        email: email.trim(),
        fleet_size: fleetSize.trim(),
        phone: phone.trim(),
      });
      setStatus(r.data?.status || "joined");
      trackConversion("waitlist_join", {
        already_on_list: !!r.data?.already_on_list,
        has_phone: !!phone.trim(),
      });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Couldn't join waitlist. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "joined" || status === "updated") {
    return (
      <div
        data-testid="pilot-waitlist-success"
        className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle size={18} weight="fill" className="text-emerald-400" />
          <div className="text-emerald-200 font-medium">
            {status === "updated" ? "You're already on the list." : "You're in."}
          </div>
        </div>
        <div className="text-sm text-emerald-100/80">
          We'll email <span className="tabular-nums">{email}</span>
          {phone.trim() && " + text you"} the moment cohort 2 opens.
          If you have a spare 15 min for a discovery call before then, just reply to the confirmation email.
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="pilot-waitlist-form"
      className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="text-white font-medium mb-1">Get on the cohort 2 waitlist</div>
      <div className="text-sm text-neutral-400 mb-4">
        Email required · phone optional (we'll text when cohort 2 opens so you don't miss it).
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@fleet.com"
          data-testid="pilot-waitlist-email"
          className="rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white text-sm placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/60"
        />
        <select
          value={fleetSize}
          onChange={(e) => setFleetSize(e.target.value)}
          data-testid="pilot-waitlist-fleet"
          className="rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 appearance-none cursor-pointer"
        >
          <option value="" className="bg-neutral-900">Fleet size…</option>
          <option value="1-2 trucks" className="bg-neutral-900">1–2 trucks</option>
          <option value="3-5 trucks" className="bg-neutral-900">3–5 trucks</option>
          <option value="6-10 trucks" className="bg-neutral-900">6–10 trucks</option>
          <option value="10+ trucks" className="bg-neutral-900">10+ trucks</option>
        </select>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone (optional · +1…)"
          data-testid="pilot-waitlist-phone"
          className="sm:col-span-2 rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white text-sm placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/60"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="pilot-waitlist-submit"
        className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-medium text-sm px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? "Joining…" : "Join waitlist"}
        {!submitting && <ArrowRight size={14} weight="bold" />}
      </button>
      {status === "error" && (
        <div
          data-testid="pilot-waitlist-error"
          className="mt-3 text-xs text-red-300"
        >
          {errorMsg}
        </div>
      )}
    </form>
  );
}


export function TestimonialsSection() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/testimonials/public")
      .then((r) => {
        if (cancelled) return;
        const merged = [...(r.data?.featured || []), ...(r.data?.quotes || [])];
        setItems(merged);
      })
      .catch(() => setItems([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Waiting for the fetch to resolve — don't render skeleton, the section
  // above/below carries the eye until we know what to draw.
  if (items === null) return null;

  // No real testimonials yet — render the honest founder-credibility section
  // instead of fabricated quotes. As soon as `/testimonials/public` returns
  // real ones this fallback disappears and the quote grid takes over.
  if (items.length === 0) {
    return (
      <section
        id="testimonials"
        data-testid="testimonials-section-pilots"
        className="border-b border-white/10 py-20 sm:py-28"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl mb-10">
            <div className="overline mb-3 text-[#FF9764]">
              Early carriers · honest by default
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Built with the carriers who <span className="text-[#FF5A1F]">use it every day</span>.
            </h2>

            <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">
              We're building FreightPilotAI alongside the small fleets it's built for. Start the
              7-day free trial (card on file, $0 today) and we'll wire your broker checks, carrier packets,
              payroll and IFTA miles together. Your feedback shapes the roadmap.
            </p>
          </div>

          {/* Three "why trust us" cards — founder credibility, honest scope, pilot terms */}
          <div className="grid gap-5 md:grid-cols-3">
            <div
              data-testid="pilot-cred-founder"
              className="rounded-xl border border-white/10 bg-white/[0.02] p-6"
            >
              <Users size={20} weight="fill" className="text-[#FF5A1F] mb-3" />
              <div className="text-white font-medium text-base mb-1.5">
                Built by a former fleet owner
              </div>
              <div className="text-sm text-neutral-400 leading-relaxed">
                Built by a former fleet owner who understands trucking operations firsthand — POD
                cycles, broker back-and-forth, and every 2 AM invoicing panic.
              </div>
            </div>

            <div
              data-testid="pilot-cred-scope"
              className="rounded-xl border border-white/10 bg-white/[0.02] p-6"
            >
              <Sparkle size={20} weight="fill" className="text-[#FF5A1F] mb-3" />
              <div className="text-white font-medium text-base mb-1.5">
                Honest about where we are
              </div>
              <div className="text-sm text-neutral-400 leading-relaxed">
                We won't fake customer quotes. Once you're a paying pilot and want to share your
                story, we'll put your name on the wall. Until then — this section stays real.
              </div>
            </div>

            <div
              data-testid="pilot-cred-terms"
              className="rounded-xl border border-white/10 bg-white/[0.02] p-6"
            >
              <CheckCircle size={20} weight="fill" className="text-[#FF5A1F] mb-3" />
              <div className="text-white font-medium text-base mb-1.5">
                7-day free trial, no tricks
              </div>
              <div className="text-sm text-neutral-400 leading-relaxed">
                Card on file, $0 today. Full access for 7 days, then the plan you picked — cancel in
                one tap from Billing before day 8 and you're never charged. Your data stays exportable.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="border-b border-white/10 py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">What pilots are saying</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Built with the carriers <span className="text-[#FF5A1F]">it was built for</span>.
          </h2>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.slice(0, 6).map((t, i) => (
            <figure
              key={t.id || i}
              data-testid={`testimonial-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-6 flex flex-col"
            >
              <Quotes size={22} weight="fill" className="text-[#FF5A1F]/50 mb-3 shrink-0" />
              <blockquote className="text-neutral-200 text-sm sm:text-base leading-relaxed flex-1">
                {t.quote || t.body || ""}
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-white/5 flex items-center gap-3">
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt=""
                    loading="lazy"
                    className="h-9 w-9 rounded-full object-cover border border-white/10"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center text-[#FF9764] text-sm font-semibold">
                    {(t.author_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="text-xs leading-tight">
                  <div className="text-white font-medium">{t.author_name || "Anonymous pilot"}</div>
                  <div className="text-neutral-500">
                    {t.author_role || t.company_name || "FreightPilot AI pilot"}
                  </div>
                </div>
                {(t.rating || 5) >= 4 && (
                  <div className="ml-auto flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, si) => (
                      <Star
                        key={si}
                        size={11}
                        weight="fill"
                        className={si < (t.rating || 5) ? "text-[#FF9764]" : "text-neutral-700"}
                      />
                    ))}
                  </div>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  FOUNDER SECTION  ───────────────────── */

/* Founder video tile — reads `REACT_APP_FOUNDER_LOOM_URL` from env. When set
   (e.g. https://www.loom.com/embed/abc123), renders the Loom iframe so a
   60-second personal message plays right on the landing page. When empty,
   falls back to the original copy tile so the layout doesn't break in
   preview/dev environments. */
export function FounderVideoTile() {
  const loomUrl = (process.env.REACT_APP_FOUNDER_LOOM_URL || "").trim();
  const [letter, setLetter] = useState(null);

  // Fetch the editable letter copy — falls back to null on error so the
  // hardcoded default paragraphs still render.
  useEffect(() => {
    if (loomUrl) return; // no need to fetch if the video will take over
    let cancelled = false;
    client
      .get("/founder-letter")
      .then((r) => {
        if (!cancelled) setLetter(r.data || null);
      })
      .catch(() => setLetter(null));
    return () => {
      cancelled = true;
    };
  }, [loomUrl]);

  // Accept either a full loom.com/share URL, a loom.com/embed URL, or a
  // bare video-id. Normalise everything to the embed form.
  const embedSrc = (() => {
    if (!loomUrl) return "";
    if (/^https?:\/\/(www\.)?loom\.com\/embed\//i.test(loomUrl)) return loomUrl;
    const shareMatch = loomUrl.match(/loom\.com\/(?:share|v)\/([a-zA-Z0-9]+)/i);
    if (shareMatch) return `https://www.loom.com/embed/${shareMatch[1]}`;
    if (/^[a-zA-Z0-9]+$/.test(loomUrl)) return `https://www.loom.com/embed/${loomUrl}`;
    return loomUrl; // trust whatever the operator set
  })();

  if (embedSrc) {
    return (
      <div
        data-testid="founder-video-tile"
        className="relative rounded-2xl overflow-hidden border border-white/10 aspect-square max-w-sm mx-auto bg-black"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF5A1F]/10 via-transparent to-[#007AFF]/10 pointer-events-none z-10" />
        <iframe
          src={embedSrc}
          title="A 60-second message from the founder"
          allow="fullscreen"
          allowFullScreen
          data-testid="founder-video-iframe"
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    );
  }

  return (
    <div
      data-testid="founder-video-tile"
      className="relative rounded-2xl overflow-hidden border border-white/10 max-w-sm mx-auto"
    >
      {/* Radial warm-orange glow behind the letter — evokes an early-morning
          desk lamp rather than a cold marketing tile. */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF5A1F]/12 via-transparent to-transparent" />
      <div
        className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-[#FF5A1F]/15 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative flex flex-col px-6 py-8 sm:py-9">
        <div className="overline text-[#FF9764] mb-4 flex items-center gap-2">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
          {letter?.overline || "A note from the founder"}
        </div>

        {/* Handwritten-style letter — italic serif, tighter line-height,
            personal voice. The italic + slight color drop reads as "written",
            not "designed". */}
        <div
          data-testid="founder-letter-body"
          className="font-serif italic text-neutral-200 text-[15px] leading-[1.65] space-y-3"
        >
          {(letter?.paragraphs || [
            "Hey — I'm Mike.",
            "Before FreightPilotAI I drove trucks, ran 6 of them, and dispatched them all myself. I know exactly what it feels like to file paperwork at midnight and get double-brokered the next morning.",
            "That's the pain I'm building this to end. If you're one of our first 5 pilots, I'll wire the software around how YOUR fleet actually runs — not a template.",
            "Reply anytime. I read every note.",
          ]).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Signature block — script-styled name, hand-drawn underline via
            SVG so it survives font-loading order without a layout jump. */}
        <div className="mt-6 pt-5 border-t border-white/5">
          <div
            data-testid="founder-signature"
            className="text-[#FF9764] text-xl leading-none"
            style={{ fontFamily: '"Caveat", "Segoe Script", "Bradley Hand", cursive' }}
          >
            {letter?.signature_name || "Mike"}
          </div>
          <div className="mt-1.5 text-[11px] uppercase tracking-widest text-neutral-500">
            {letter?.signature_title || "Founder · Sioux Falls, SD"}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FounderSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="border-b border-white/10 py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="overline mb-3 text-[#FF9764]">The founder's story</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-6">
              I was you —{" "}
              <span className="text-[#FF5A1F]">for years</span>.
            </h2>
            <div className="text-neutral-300 text-base sm:text-lg leading-relaxed space-y-4">
              <p>
                Before I built FreightPilotAI, I ran trucks. I was an owner-operator, then a small fleet owner with <span className="text-white font-medium">6 trucks</span> — and I was still driving one of them myself while trying to dispatch the other five from the cab.
              </p>
              <p>
                Every night was paperwork I didn't have time for. Every breakdown was a 3am scramble. Every DOT inspection was <span className="text-white">one small maintenance slip</span> from taking the whole company down. And the brokers — treated us like we were nothing. Double-brokering loads to make an extra hundred bucks. Ghosting on invoices. Pressuring on rates like we didn't have families to feed.
              </p>
              <p>
                That's why I built this — not from a whiteboard in Silicon Valley, but from <span className="text-white font-medium">Sioux Falls, SD</span>, after <span className="text-white">50+ discovery calls</span> with other owner-operators and small fleets confirmed I wasn't the only one drowning.
              </p>
              <p>
                FreightPilotAI is the tool I wish I had when I was doing 100k miles a year and dispatching 6 trucks at 2am. When you sign up, <span className="text-white font-medium">I'm still the one</span> on the 30-min Zoom setting it up while you watch. The founder is one phone call away: <a href="tel:+16056797303" className="text-[#FF9764] hover:text-[#FF5A1F] underline decoration-dotted underline-offset-4">(605) 679-7303</a>.
              </p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="tel:+16056797303"
                data-testid="about-call-founder"
                className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                Call Mike — (605) 679-7303
                <ArrowRight size={14} weight="bold" />
              </a>
              <Link
                to="/services"
                data-testid="about-learn-more"
                className="rounded-full px-5 py-3 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                What we automate
                <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <FounderVideoTile />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  FAQ  ───────────────────── */

export function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="border-b border-white/10 py-20 sm:py-28"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="mb-10 sm:mb-14">
          <div className="overline mb-3 text-[#FF9764]">FAQ</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Answers before you ask.
          </h2>
        </div>
        <div className="space-y-2">
          {FAQS.map((f, i) => (
            <FAQItem
              key={f.q}
              q={f.q}
              a={f.a}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
              testid={`faq-item-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQItem({ q, a, open, onToggle, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden transition-colors hover:border-white/20"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60"
        aria-expanded={open}
      >
        <span className="font-medium text-white text-sm sm:text-base">{q}</span>
        <CaretDown
          size={16}
          className={`shrink-0 text-neutral-500 transition-transform duration-200 ${open ? "rotate-180 text-[#FF5A1F]" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-6 text-neutral-400 text-sm leading-relaxed border-t border-white/5 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────  FINAL CTA  ───────────────────── */

export function FinalCTA() {
  return (
    <section
      id="cta"
      data-testid="final-cta-section"
      className="py-20 sm:py-28"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-6">
          Ready to reclaim your{" "}
          <span className="text-[#FF5A1F]">first 12 hours</span>?
        </h2>
        <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
          Tell us the workflow that eats the most time in your operation.
          We'll automate it for you — free of charge, live in ≤ 5 business days.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/contact?intent=free-workflow"
            onClick={() => trackConversion("free_workflow_cta", { position: "final_cta" })}
            data-testid="final-cta-primary"
            className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Get One Workflow Automated Free
            <ArrowRight size={16} weight="bold" />
          </Link>
          <Link
            to="/contact"
            data-testid="final-cta-secondary"
            className="rounded-full px-6 py-3.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Contact FreightPilotAI
            <PaperPlaneRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

